import React, { useCallback, useMemo } from 'react';
import { ChartPane, chartContext } from '../../ui/chart/ChartPane';
import { Group } from '@visx/group';
import { Dictionary } from 'lodash';
import {
  ChartSeriesV2,
  DataRange,
  SeriesValue,
  TimeRange,
  ChartContextData,
} from 'src/components/ui/chart/types';
import { Text } from '@visx/text';
import { makeStyles, Theme } from '@material-ui/core';
import { TimeSeries, TagMeaning } from 'src/models';
import Bars, { BarColor } from './Bars';
import ChartContainer from './ChartContainer';
import { useRanges } from './useRanges';
import { Legend } from './ChartLegend';
import { Text as HtmlText } from '../../ui/typography/';
import { parseBitfieldPoint, getBeforeStartRangePoint } from './chartUtils';
import { TEXT_COLOR, TEXT_HEIGHT, GROUP_HEIGHT, SPACE_BETWEEN_GROUPS } from './constants';
import { ChartsCount } from './AnalysisChart';

interface Props {
  lineGroups: Dictionary<ChartSeriesV2[]>;
  snapshots: Dictionary<TimeSeries>;
  fullRangeSnapshots: Dictionary<TimeSeries>;
  tagIds: string[];
  xRange: {
    start: Date;
    end: Date;
  };
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  fullRanges: DataRange[];
  zoomRange?: TimeRange;
  onZoomChange: (range: TimeRange | undefined) => void;
  color: BarColor;
  isBitfield?: boolean;
  editPointsHandler: () => void;
  removeTagFromChartGroup: (tagId: string) => void;
  triggeredOnly: boolean;
  chartsCount: ChartsCount;
  removeChartInView: () => void;
  chartLabel: string | null;
}

const useStyles = makeStyles<Theme, { color: BarColor }>((theme) => ({
  bar: {
    transition: 'opacity 0.1s ease',
    '&:hover': {
      opacity: '1 !important',
      '& rect': {
        opacity: '1 !important',
      },
    },
  },

  group: {
    '&:hover': {
      '& rect': {
        opacity: 0.5,
      },
    },
  },
  legend1: { backgroundColor: (props) => props.color },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
}));

interface MeaningBarProps {
  meaning: TagMeaning;
  index: number;
  group: ChartSeriesV2;
  parsedPoint: ChartSeriesV2[] | null;
  beforeStartRangePoint: SeriesValue | SeriesValue[] | undefined;
  context: ChartContextData;
  chartRanges: DataRange[];
  fullRanges: DataRange[];
  color: BarColor;
  classes: ReturnType<typeof useStyles>;
}

interface ChartGroupProps {
  point: ChartSeriesV2;
  snapshots: Dictionary<TimeSeries>;
  fullRangeSnapshots: Dictionary<TimeSeries>;
  fullRanges: DataRange[];
  chartRanges: DataRange[];
  isBitfield: boolean;
  triggeredOnly: boolean;
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  removeTagFromChartGroup: (tagId: string) => void;
  editPointsHandler: () => void;
  color: BarColor;
  classes: ReturnType<typeof useStyles>;
  chartsCount: ChartsCount | undefined;
  chartIndex: number;
  removeChartInView: () => void;
  chartLabel: string | null;
}

const MeaningBar = React.memo<MeaningBarProps>(
  ({
    meaning,
    index,
    group,
    beforeStartRangePoint,
    context,
    chartRanges,
    fullRanges,
    color,
    classes,
  }) => {
    const top = GROUP_HEIGHT * index + SPACE_BETWEEN_GROUPS;

    return (
      <Group key={meaning.description} top={top}>
        <Text fill={TEXT_COLOR} verticalAnchor="middle" fontSize={10}>
          {meaning.description.replace(/_/g, ' ')}
        </Text>
        <Group top={TEXT_HEIGHT} left={0} className={classes.bar}>
          <Bars
            group={group}
            beforeStartRangePoint={
              Array.isArray(beforeStartRangePoint)
                ? beforeStartRangePoint[Number(meaning.value)]
                : beforeStartRangePoint
            }
            context={context}
            ranges={chartRanges}
            fullRanges={fullRanges}
            meaningId={Number(meaning.value)}
            color={color}
          />
        </Group>
      </Group>
    );
  }
);

const ChartGroup = React.memo<ChartGroupProps>(
  ({
    point,
    snapshots,
    fullRangeSnapshots,
    fullRanges,
    chartRanges,
    isBitfield,
    triggeredOnly,
    margins,
    removeTagFromChartGroup,
    editPointsHandler,
    color,
    classes,
    chartsCount,
    chartIndex,
    removeChartInView,
    chartLabel,
  }) => {
    const chartSnapshots = useMemo(
      () => ({
        [point.key]: { ...snapshots[point.key], lineClass: '', legendClass: classes.legend1 },
      }),
      [point.key, snapshots, classes.legend1]
    );

    const beforeStartRangePoint = useMemo(
      () => getBeforeStartRangePoint(point, fullRangeSnapshots, fullRanges, isBitfield),
      [point, fullRangeSnapshots, fullRanges, isBitfield]
    );

    const parsedPoint = useMemo(
      () => (isBitfield ? parseBitfieldPoint(point) : null),
      [point, isBitfield]
    );
    const { displayMeanings, hiddenCount } = useMemo(() => {
      const originalMeanings = point.meanings || [];

      const activeMeanings = originalMeanings.filter((meaning) => {
        const meaningValue = Number(meaning.value);

        if (isBitfield && parsedPoint) {
          if (Array.isArray(parsedPoint)) {
            return parsedPoint[Number(meaning.value)].values.some((value) => {
              const valueY = typeof value.y === 'number' ? value.y : Number(value.y);
              return valueY === meaningValue;
            });
          }
          return false;
        }

        if (beforeStartRangePoint && !Array.isArray(point)) {
          let hasMeaning = false;
          point.values.forEach((value) => {
            const valueY = typeof value.y === 'number' ? value.y : Number(value.y);
            if (valueY === meaningValue) {
              hasMeaning = true;
            }
          });
          return hasMeaning;
        }
        return true;
      });

      return {
        displayMeanings: triggeredOnly ? activeMeanings : originalMeanings,
        hiddenCount:
          originalMeanings.length -
          (triggeredOnly ? activeMeanings.length : originalMeanings.length),
      };
    }, [point, triggeredOnly, isBitfield, parsedPoint, beforeStartRangePoint]);

    const meaningLength = displayMeanings.length;
    const meaningHeight = meaningLength * GROUP_HEIGHT + SPACE_BETWEEN_GROUPS;

    const setHighlighted = useCallback(() => {}, []);
    const toggleActive = useCallback(() => {}, []);
    const active = useMemo(() => Object.keys(chartSnapshots), [chartSnapshots]);
    const chartNumber =
      chartIndex + 1 + (chartsCount?.chart || 0) + (isBitfield ? 0 : chartsCount?.bitfield || 0);
    const title = chartLabel ? chartLabel : `Chart ${chartNumber} of ${chartsCount?.total}`;
    return (
      <ChartContainer key={point.key} title={title} onRemove={removeChartInView}>
        <HtmlText fontWeight={500}>{snapshots[point.key].tag.label}</HtmlText>
        <HtmlText fontSize={12}>
          {triggeredOnly &&
            point.meanings &&
            `${hiddenCount} value${hiddenCount > 1 ? 's' : ''} hidden`}
        </HtmlText>
        <ChartPane
          key={point.key}
          height={meaningHeight + SPACE_BETWEEN_GROUPS}
          range={chartRanges[0]}
          margins={margins}
        >
          <chartContext.Consumer>
            {(chartCtx) => (
              <Group className={classes.group}>
                {displayMeanings.map((meaning, j) => {
                  const meaningIndex = Number(meaning.value);
                  const group =
                    isBitfield && Array.isArray(parsedPoint) ? parsedPoint[meaningIndex] : point;
                  return (
                    <MeaningBar
                      key={meaning.description}
                      meaning={meaning}
                      index={j}
                      group={group}
                      parsedPoint={parsedPoint}
                      beforeStartRangePoint={beforeStartRangePoint}
                      context={chartCtx}
                      chartRanges={chartRanges}
                      fullRanges={fullRanges}
                      color={color}
                      classes={classes}
                    />
                  );
                })}
              </Group>
            )}
          </chartContext.Consumer>
        </ChartPane>
        <Legend
          snapshots={chartSnapshots}
          setHighlighted={setHighlighted}
          toggleActive={toggleActive}
          active={active}
          removeTagFromChartGroup={removeTagFromChartGroup}
          editPointsHandler={editPointsHandler}
        />
      </ChartContainer>
    );
  }
);

export const BarsChart: React.FC<Props> = ({
  lineGroups,
  snapshots,
  fullRangeSnapshots,
  tagIds,
  xRange,
  margins,
  fullRanges,
  zoomRange,
  color,
  isBitfield = false,
  editPointsHandler,
  removeTagFromChartGroup,
  triggeredOnly,
  chartsCount,
  chartLabel,
}) => {
  const classes = useStyles({ color });
  const { ranges } = useRanges(tagIds, snapshots, xRange, zoomRange);
  const chartRanges = isBitfield ? ranges.bitfield : ranges.enum;

  if (!Object.keys(lineGroups).length) {
    return null;
  }

  // the bar chart only displays one tag. If we remove the chart we could be removing more than one chart
  // the lines chart can display more than one tag in the same chart
  const removeChartHandler = () => {
    removeTagFromChartGroup(tagIds[0]);
  };

  let totalPoints = 0;
  Object.values(lineGroups).forEach((points) => {
    totalPoints += points.length;
  });

  return (
    <div className={classes.container}>
      {Object.entries(lineGroups).map(([_unit, points]) =>
        points.map((point, pointIndex) => {
          const chartIndex = totalPoints > 0 ? pointIndex : 0;
          return (
            <ChartGroup
              chartLabel={chartLabel}
              key={point.key}
              chartIndex={chartIndex}
              point={point}
              snapshots={snapshots}
              fullRangeSnapshots={fullRangeSnapshots}
              fullRanges={fullRanges}
              chartRanges={chartRanges}
              isBitfield={isBitfield}
              triggeredOnly={triggeredOnly}
              margins={margins}
              removeTagFromChartGroup={removeTagFromChartGroup}
              editPointsHandler={editPointsHandler}
              color={color}
              classes={classes}
              chartsCount={chartsCount}
              removeChartInView={removeChartHandler}
            />
          );
        })
      )}
    </div>
  );
};

export default BarsChart;
